import { datadogRum } from '@datadog/browser-rum';

import { logger } from '../logger';
import { emitPerformanceDebugEvent } from './debug-sink';
import {
  classifySessionFlowFailure,
  getClientPlatform,
  getMetricOperation,
  sessionFlowDimensions,
} from './metric-dimensions';
import {
  type CaptureTransport,
  TRACKING_TYPE_BY_KEY,
  type TrackingEntry,
  type TrackingHandle,
  type TrackingKey,
  type TrackingResult,
} from './types';

const LOG_PREFIX = '[PerformanceTracking]';

// Singleton store: one entry per key. The default, fire-by-key path.
const activeTrackings = new Map<TrackingKey, TrackingEntry>();
// Instanced store: keyed by unique instance id so concurrent runs of the same
// key don't clobber each other. Populated only via `startTracking({ instanced: true })`.
const instancedTrackings = new Map<number, TrackingEntry>();
let trackingInstanceCounter = 0;

/**
 * Targets a tracking entry by key (singleton store) or handle (instanced
 * store). The `?: never` arms make the two mutually exclusive at compile time:
 * passing a handle to a singleton flow — or both — is a type error.
 */
type Target =
  | { key: TrackingKey; handle?: never }
  | { handle: TrackingHandle; key?: never };

/** How the flow ended; a failure carries the thrown value for `error.type`. */
type TerminalOutcome =
  | { outcome?: 'completed'; error?: never }
  | { outcome: 'failed'; error: unknown };

function targetKey(target: Target): TrackingKey {
  return target.handle != null ? target.handle.key : target.key;
}

function getEntry(target: Target): TrackingEntry | undefined {
  return target.handle != null
    ? instancedTrackings.get(target.handle.instanceId)
    : activeTrackings.get(target.key);
}

function setEntry(target: Target, entry: TrackingEntry): void {
  if (target.handle != null) instancedTrackings.set(target.handle.instanceId, entry);
  else activeTrackings.set(target.key, entry);
}

function deleteEntry(target: Target): void {
  if (target.handle != null) instancedTrackings.delete(target.handle.instanceId);
  else activeTrackings.delete(target.key);
}

function createPerformanceTimeline(
  entry: Pick<TrackingEntry, 'key' | 'instanceId' | 'userTiming'>
) {
  const prefix = `${entry.key}#${entry.instanceId}`;
  // A flow that opted out leaves nothing in the user-timing buffer. Nothing
  // in the app ever clears that buffer, so a high-frequency instanced flow on
  // a days-open desktop document would otherwise grow it without bound.
  const silent = entry.userTiming === false;

  return {
    start: `${prefix}:start`,
    end: `${prefix}:end`,
    checkpoint: (checkpointName: string, checkpointIndex: number) =>
      `${prefix}:checkpoint:${checkpointIndex}:${checkpointName}`,
    mark: (name: string, startTime: number) => {
      if (silent || typeof performance.mark !== 'function') return;
      performance.mark(name, { startTime });
    },
    measure: (label: string, startMark: string, endMark: string) => {
      if (silent || typeof performance.measure !== 'function') return;
      performance.measure(`${entry.key}: ${label}`, startMark, endMark);
    },
    clearMark: (name: string) => {
      if (typeof performance.clearMarks !== 'function') return;
      performance.clearMarks(name);
    },
    clearMeasure: (label: string) => {
      if (typeof performance.clearMeasures !== 'function') return;
      performance.clearMeasures(`${entry.key}: ${label}`);
    },
  };
}

// Only instanced keys fire per attempt at a cadence that grows the User Timing
// buffer for the life of the document. Singleton keys keep their entries,
// which DevTools and the tab-switch benchmark read after the flow ends.
function clearsTimeline(target: Target): boolean {
  return target.handle != null;
}

function clearPerformanceMarks(entry: TrackingEntry): void {
  const timeline = createPerformanceTimeline(entry);
  timeline.clearMark(timeline.start);
  entry.checkpoints.forEach((checkpoint, i) => {
    timeline.clearMark(timeline.checkpoint(checkpoint.name, i));
  });
  timeline.clearMark(timeline.end);
}

function recordPerformanceMeasures(
  entry: TrackingEntry,
  endTime: number,
  options: { clearAfterMeasuring: boolean }
): void {
  if (
    entry.userTiming === false ||
    typeof performance.mark !== 'function' ||
    typeof performance.measure !== 'function'
  ) {
    if (options.clearAfterMeasuring) clearPerformanceMarks(entry);
    return;
  }

  const timeline = createPerformanceTimeline(entry);
  const measureLabels: string[] = [];
  const measure = (label: string, startMark: string, endMark: string) => {
    measureLabels.push(label);
    timeline.measure(label, startMark, endMark);
  };

  try {
    timeline.mark(timeline.end, endTime);
    measure('total', timeline.start, timeline.end);

    let previousMarkName = timeline.start;

    entry.checkpoints.forEach((checkpoint, i) => {
      const checkpointMarkName = timeline.checkpoint(checkpoint.name, i);
      const phaseMeasureName =
        i === 0
          ? `phase:start -> ${checkpoint.name}`
          : `phase:${entry.checkpoints.at(i - 1)!.name} -> ${checkpoint.name}`;

      measure(`checkpoint:${checkpoint.name}`, timeline.start, checkpointMarkName);
      measure(phaseMeasureName, previousMarkName, checkpointMarkName);

      previousMarkName = checkpointMarkName;
    });

    const lastCheckpoint = entry.checkpoints.at(-1);
    const lastPhaseName = lastCheckpoint
      ? `${lastCheckpoint.name} -> end`
      : 'start -> end';
    measure(`phase:${lastPhaseName}`, previousMarkName, timeline.end);
  } finally {
    if (options.clearAfterMeasuring) {
      measureLabels.forEach((label) => timeline.clearMeasure(label));
      clearPerformanceMarks(entry);
    }
  }
}

/** Returns a read-only view of the singleton (fire-by-key) tracking entries. */
export function getActiveTrackings(): ReadonlyMap<TrackingKey, TrackingEntry> {
  return activeTrackings;
}

/**
 * Returns every in-flight entry across both the singleton and instanced
 * stores. Used by the abandonment reporter so leaked/in-flight instanced runs
 * are snapshotted on page exit, not just singleton ones.
 */
export function getAllTrackingEntries(): readonly TrackingEntry[] {
  return [...activeTrackings.values(), ...instancedTrackings.values()];
}

/** Clears all active trackings. Intended for test cleanup. */
export function resetActiveTrackings(): void {
  activeTrackings.clear();
  instancedTrackings.clear();
  trackingInstanceCounter = 0;
}

/**
 * Derives a {@link TrackingResult} from a completed entry.
 *
 * Produces three timing breakdowns:
 * - `checkpointDurationsMs` — elapsed time from start to each checkpoint.
 * - `phaseDurationsMs` — elapsed time between consecutive checkpoints (or start/end).
 * - `totalDurationMs` — wall-clock time from start to end.
 */
export function computeResult(entry: TrackingEntry, endTime: number): TrackingResult {
  const checkpointDurationsMs: Record<string, number> = {};
  const phaseDurationsMs: Record<string, number> = {};

  let previousTime = entry.startTime;

  entry.checkpoints.forEach((checkpoint, i) => {
    checkpointDurationsMs[checkpoint.name] = checkpoint.timestamp - entry.startTime;

    const phaseName =
      i === 0
        ? `start -> ${checkpoint.name}`
        : `${entry.checkpoints.at(i - 1)!.name} -> ${checkpoint.name}`;
    phaseDurationsMs[phaseName] = checkpoint.timestamp - previousTime;

    previousTime = checkpoint.timestamp;
  });

  const lastCheckpoint = entry.checkpoints.at(-1);
  const lastPhaseName = lastCheckpoint ? `${lastCheckpoint.name} -> end` : 'start -> end';
  phaseDurationsMs[lastPhaseName] = endTime - previousTime;

  return {
    key: entry.key,
    type: TRACKING_TYPE_BY_KEY[entry.key],
    totalDurationMs: endTime - entry.startTime,
    checkpointDurationsMs,
    phaseDurationsMs,
    metadata: entry.metadata,
  };
}

/**
 * Begins tracking a new performance flow.
 *
 * Singleton mode (default): only one active tracking per key is allowed; a
 * second start for the same key overwrites the first with a warning. Returns
 * `void`.
 *
 * Instanced mode (`instanced: true`): the run is stored under a unique instance
 * id and never collides with other runs of the same key. Returns a
 * {@link TrackingHandle} that must be threaded to `appendCheckpoint` /
 * `finishTracking` / `cancelTracking` to act on this exact run.
 */
export function startTracking(params: {
  key: TrackingKey;
  metadata?: Record<string, unknown>;
  instanced: true;
  userTiming?: boolean;
}): TrackingHandle;
export function startTracking(params: {
  key: TrackingKey;
  metadata?: Record<string, unknown>;
  instanced?: false;
  userTiming?: boolean;
}): void;
export function startTracking(params: {
  key: TrackingKey;
  metadata?: Record<string, unknown>;
  instanced?: boolean;
  /**
   * `false` writes no `performance.mark` / `performance.measure` for this
   * run. Default on: the DevTools timeline is what the marks are for. Opt out
   * for a flow that runs many times per hour for the life of the document.
   */
  userTiming?: boolean;
}): TrackingHandle | void {
  const instanceId = ++trackingInstanceCounter;
  const startTime = performance.now();
  const entry: TrackingEntry = {
    key: params.key,
    instanceId,
    startTime,
    checkpoints: [],
    metadata: params.metadata,
    ...(params.userTiming === false ? { userTiming: false as const } : {}),
  };

  if (params.instanced) {
    instancedTrackings.set(instanceId, entry);
  } else {
    if (activeTrackings.has(params.key)) {
      logger.warn(`${LOG_PREFIX} Overwriting existing tracking entry: ${params.key}`);
    }
    activeTrackings.set(params.key, entry);
  }

  const timeline = createPerformanceTimeline(entry);
  timeline.mark(timeline.start, startTime);

  emitPerformanceDebugEvent({
    type: 'start',
    key: params.key,
    detail: params.metadata ? JSON.stringify(params.metadata) : undefined,
  });

  if (params.instanced) {
    return { key: params.key, instanceId } as TrackingHandle;
  }
}

/**
 * Records a named checkpoint on an active tracking.
 *
 * Metadata provided here is shallow-merged into the entry's existing metadata.
 * Warns and no-ops if no active tracking exists for the given key.
 */
export function appendCheckpoint(
  params: Target & {
    checkpointName: string;
    metadata?: Record<string, unknown>;
  }
): void {
  const entry = getEntry(params);

  if (!entry) {
    // A miss is expected for callers that annotate whichever flow happens to
    // be live (route listeners, layout stoppers) and is not worth a Datadog
    // log line on every navigation — `info` forwards to Datadog Logs in prod.
    logger.debug(
      `${LOG_PREFIX} No active tracking found for checkpoint: ${targetKey(params)}`
    );
    return;
  }

  const now = performance.now();
  const timeline = createPerformanceTimeline(entry);
  timeline.mark(
    timeline.checkpoint(params.checkpointName, entry.checkpoints.length),
    now
  );

  setEntry(params, {
    ...entry,
    checkpoints: [...entry.checkpoints, { name: params.checkpointName, timestamp: now }],
    metadata: params.metadata
      ? { ...entry.metadata, ...params.metadata }
      : entry.metadata,
  });

  emitPerformanceDebugEvent({
    type: 'checkpoint',
    key: entry.key,
    detail: params.checkpointName,
    elapsedMs: now - entry.startTime,
  });
}

/**
 * Stamps the capture engine that runs an open attempt; a later stamp replaces
 * it. No-ops when no entry is open for the target.
 */
export function setCaptureTransport(
  params: Target & { transport: CaptureTransport }
): void {
  const entry = getEntry(params);
  if (!entry) return;
  setEntry(params, { ...entry, captureTransport: params.transport });
}

/**
 * Terminates an active tracking, computes the result, and reports it to Datadog RUM.
 *
 * Returns the computed {@link TrackingResult}, or `undefined` if no active
 * tracking was found for the key.
 */
export function finishTracking(
  params: Target & {
    metadata?: Record<string, unknown>;
    /**
     * Completion timestamp in the `performance.now()` domain. Pass this when the
     * RUM call is deliberately deferred — e.g. while waiting on resource timing
     * that lands after the flow finished — so `totalDurationMs` reflects when
     * the flow actually ended, not when telemetry got around to reporting it.
     */
    endTime?: number;
  } & TerminalOutcome
): TrackingResult | undefined {
  const entry = getEntry(params);

  if (!entry) {
    logger.debug(
      `${LOG_PREFIX} No active tracking found to finish: ${targetKey(params)}`
    );
    return;
  }

  let result: TrackingResult | undefined;
  try {
    const finalEntry = params.metadata
      ? { ...entry, metadata: { ...entry.metadata, ...params.metadata } }
      : entry;

    const endTime = params.endTime ?? performance.now();
    try {
      recordPerformanceMeasures(finalEntry, endTime, {
        clearAfterMeasuring: clearsTimeline(params),
      });
    } catch (error) {
      logger.warn(
        `${LOG_PREFIX} Could not record performance measures: ${entry.key}, errorName: ${error instanceof Error ? error.constructor.name : typeof error}`
      );
    }
    result = computeResult(finalEntry, endTime);
    // Session-flow keys carry the contract dimensions; other keys keep the plain
    // completed/failed outcome. A failed session-flow attempt reclassifies into
    // failed/blocked/cancelled and a semantic error type from the thrown value.
    const dimensions = sessionFlowDimensions(result.key, entry.captureTransport);
    const { operation } = dimensions;
    const failure =
      operation && params.outcome === 'failed'
        ? classifySessionFlowFailure(params.error, operation)
        : undefined;
    const outcome = operation
      ? (failure?.outcome ?? 'success')
      : (params.outcome ?? 'completed');
    const errorType = failure?.errorType;

    datadogRum.addAction(result.key, {
      type: result.type,
      totalDurationMs: result.totalDurationMs,
      checkpointDurationsMs: result.checkpointDurationsMs,
      phaseDurationsMs: result.phaseDurationsMs,
      metadata: result.metadata,
      ...dimensions,
      outcome,
      ...(errorType != null ? { error: { type: errorType } } : {}),
    });
    const total = `total: ${result.totalDurationMs.toFixed(0)}ms`;
    emitPerformanceDebugEvent({
      type: 'finish',
      key: result.key,
      detail: errorType != null ? `${outcome} (${errorType}), ${total}` : total,
      elapsedMs: result.totalDurationMs,
    });
    return result;
  } catch (error) {
    logger.warn(
      `${LOG_PREFIX} Could not finish terminal action: ${entry.key}, errorName: ${error instanceof Error ? error.constructor.name : typeof error}`
    );
    return result;
  } finally {
    deleteEntry(params);
  }
}

const SESSION_FLOW_INTENT_ACTION = 'perf.scribe.session_flow_intent';

/**
 * Emits a session-flow intent action before the operation runs: operation and
 * client_platform, no outcome, so it feeds the intent metric not attempt.
 * No-ops for a non-session-flow key. One emit per call; the caller debounces.
 */
export function recordSessionFlowIntent(key: TrackingKey): void {
  const operation = getMetricOperation(key);
  if (!operation) return;

  const platform = getClientPlatform();
  try {
    datadogRum.addAction(SESSION_FLOW_INTENT_ACTION, {
      operation,
      ...(platform ? { client_platform: platform } : {}),
    });
  } catch {
    logger.warn(`${LOG_PREFIX} Could not emit intent action: ${key}`);
  }
}

/** Silently removes an active tracking without reporting to Datadog. */
export function cancelTracking(params: Target): void {
  const entry = getEntry(params);
  deleteEntry(params);

  if (entry && clearsTimeline(params)) {
    try {
      clearPerformanceMarks(entry);
    } catch (error) {
      logger.warn(
        `${LOG_PREFIX} Could not clear performance marks: ${entry.key}, errorName: ${error instanceof Error ? error.constructor.name : typeof error}`
      );
    }
  }

  emitPerformanceDebugEvent({
    type: 'cancel',
    key: targetKey(params),
  });
}
